// Malay (Bahasa Malaysia, "anda") copy for the pricing pages. Same shape as copy.en.ts (the source of truth); keep
// the two in step. Plan names (Lite, Pro, Max, Pay as you go, Enterprise) are product names and stay in English, as
// they do in the app. "FTP atas permintaan", "pemuat naik web", "pratonton dahulu" and "ruang kerja" as on
// /on-demand-ftp and /preview-first-uploads.

import type { PricingCopy } from "./copy.en";

const ms: PricingCopy = {
  common: {
    perMonth: "/bulan",
    onDemandFtpLink: "Cara FTP atas permintaan berfungsi",
    aMonth: "sebulan",
    today: "Sekarang",
    fromChangeDate: "Mulai 1 November",
    changeDate: "1 November 2026",
    gb: "{n} GB",
    tb: "{n} TB",
    mostPopular: "Paling popular",
    renamedPlan: "{from} (kini dinamakan {to})",
    planFeatures: {
      free: [
        {
          text: "Jenama anda sendiri, “Powered by Photohawk”",
          note: "Logo dan warna anda pada setiap galeri. Lencana kecil “Powered by Photohawk” dipaparkan di bahagian bawah galeri anda dan halaman log masuk ruang kerja anda. Pro dan Max tiada lencana.",
        },
        "Pasukan sehingga 3 orang",
        "Sokongan pusat bantuan",
        {
          text: "Pelayan FTP atas permintaan",
          note: "Jika FTP amat diperlukan, mulakan pelayan anda sendiri daripada pemuat naik web, pada alamat Photohawk anda sendiri yang tidak pernah berubah. Ia dimatikan secara automatik selepas 1 jam tanpa aktiviti.",
        },
        {
          text: "Muat naik pratonton dahulu",
          note: "Galeri dalam talian dengan pratonton ringan dalam beberapa minit sementara fail asal menyusul, untuk jualan dan muat turun percuma.",
        },
      ],
      pro: [
        {
          text: "Jenama anda sendiri",
          note: "Logo dan warna anda, tanpa lencana Photohawk.",
        },
        "Domain tersuai",
        "Pasukan sehingga 10 orang",
        "Analitik pelawat",
        {
          text: "Pelayan FTP atas permintaan",
          note: "Jika FTP amat diperlukan, mulakan pelayan anda sendiri daripada pemuat naik web, pada alamat Photohawk anda sendiri yang tidak pernah berubah. Ia dimatikan secara automatik selepas 1 jam tanpa aktiviti.",
        },
        {
          text: "Muat naik pratonton dahulu",
          note: "Galeri dalam talian dengan pratonton ringan dalam beberapa minit sementara fail asal menyusul, untuk jualan dan muat turun percuma.",
        },
        "Sokongan e-mel",
      ],
      max: [
        "Semua dalam Pro",
        "Muat naik video",
        "Pembahagian hasil",
        "Pelbagai jenama",
        "Ahli pasukan dan peranan tanpa had",
        "Analitik pelawat",
        {
          text: "Pelayan FTP atas permintaan",
          note: "Jika FTP amat diperlukan, mulakan pelayan anda sendiri daripada pemuat naik web, pada alamat Photohawk anda sendiri yang tidak pernah berubah. Ia dimatikan secara automatik selepas 1 jam tanpa aktiviti.",
        },
        {
          text: "Muat naik pratonton dahulu",
          note: "Galeri dalam talian dengan pratonton ringan dalam beberapa minit sementara fail asal menyusul, untuk jualan dan muat turun percuma.",
        },
        "Sokongan keutamaan",
      ],
    },
    planTaglines: {
      free: "Untuk bermula dan acara sekali-sekala.",
      pro: "Untuk ruang kerja yang kerap menjual di bawah jenama sendiri.",
      max: "Untuk studio, pasukan dan volum tinggi: kadar terendah, storan paling banyak, video dan sokongan keutamaan.",
    },
    countries: {
      uk: "United Kingdom",
      my: "Malaysia",
      ph: "Filipina",
      mx: "Mexico",
      co: "Colombia",
      eur: "Portugal dan zon euro",
      us: "Amerika Syarikat",
      ca: "Kanada",
    },
    inCountry: {
      uk: "di United Kingdom",
      my: "di Malaysia",
      ph: "di Filipina",
      mx: "di Mexico",
      co: "di Colombia",
      eur: "di Portugal dan zon euro",
      us: "di Amerika Syarikat",
      ca: "di Kanada",
    },
  },

  pricing: {
    seo: {
      title: "Harga | Photohawk",
      description:
        "Kadar lebih rendah pada setiap pelan untuk fotografi acara: 16% pada Lite, 14% pada Pro dan 12% pada Max. Storan dan muat turun percuma disertakan.",
    },
    notice: "Harga baharu mulai 1 November 2026.",
    noticeLink: "Lihat perubahannya",
    hero: {
      badge: "Harga",
      title: "Kadar lebih rendah pada setiap pelan.",
      titleHighlight: "Serendah {rate}.",
      subtitle:
        "Jual pada 16% dengan Lite, 14% dengan Pro atau 12% dengan Max. Yuran transaksi berfungsi sama seperti hari ini.",
      events: "Pelan yang sama untuk setiap jenis acara: larian dan kayuhan, perlawanan, persembahan, festival, majlis graduasi dan persidangan.",
    },
    rate: {
      heading: "Komisen anda pada setiap pelan",
      planLabel: "pada {plan}",
      points: [
        "Yuran kad dikenakan berasingan dan berfungsi sama seperti hari ini.",
        "Komisen kami ialah satu-satunya yuran Photohawk pada sesuatu jualan.",
        "Perjanjian khas sedia ada diteruskan sehingga tamat.",
      ],
    },
    plans: {
      heading: "Pilih pelan anda",
      subtitle:
        "Pelan bulanan dalam dolar AS, tanpa kontrak. Setiap peningkatan pelan menurunkan kadar anda dan menambah storan serta muat turun percuma.",
      sellingLabel: "Jualan",
      sellingLine: "Komisen {rate} pada jualan",
      sellingNote: "Ditambah yuran transaksi, seperti hari ini",
      freeDownloadsLabel: "Muat turun percuma",
      freeDownloadsLine: "{count} foto sebulan disertakan",
      overageLine: "kemudian $8 setiap 1,000 foto",
      storageLabel: "Storan",
      storageLine: "{storage} disertakan",
      storageNote: "kemudian pek dari $5 sebulan",
      includedLabel: "Termasuk",
      cta: "Mula sekarang",
      ctaFor: "dengan {plan}",
      planChange:
        "Menukar pelan: kadar lebih rendah sesuatu pelan berkuat kuasa mulai bulan penuh pertama anda padanya. Penurunan pelan berkuat kuasa pada akhir bulan.",
    },
    standard: {
      heading: "Standard pada setiap pelan",
      subtitle: "Setiap pelan mendapat semua ini, termasuk Lite pada $0 sebulan.",
      items: [
        { id: "selfie", title: "Carian swafoto", body: "Pembeli mengambil swafoto dan melihat setiap foto yang ada mereka." },
        { id: "bib", title: "Carian nombor bib dan masa", body: "Cari foto mengikut nombor perlumbaan atau masa foto diambil." },
        { id: "preview", title: "Perlindungan pratonton", body: "Perlindungan tangkapan skrin dengan ketik untuk mendedahkan, supaya pratonton menghasilkan jualan dan bukan disalin." },
        {
          id: "uploader",
          title: "Pemuat naik web dan Photohawk Studio",
          body: "Cara standard untuk memuat naik: ia menghantar banyak foto serentak, menyesuaikan diri dengan sambungan anda dan bersambung semula dengan sendirinya. Pada setiap pelan, pratonton ringan boleh dimuat naik dahulu supaya galeri anda dalam talian dalam beberapa minit. Studio berjalan dalam pelayar web anda hari ini; aplikasi Photohawk Studio untuk Mac, iPhone dan iPad dilancarkan pada November 2026.",
        },
        { id: "whatsapp", title: "Penghantaran WhatsApp, perkhidmatan Photohawk untuk pembeli", body: "Pembeli boleh memilih untuk membayar sedikit lebih semasa pembayaran untuk menerima foto mereka di WhatsApp, sedia untuk dikongsi. Photohawk menjalankannya pada setiap galeri: tiada apa-apa untuk anda sediakan, tetapkan harga atau matikan." },
      ],
    },
    storage: {
      heading: "Storan",
      subtitle:
        "Setiap pelan termasuk storan. Simpan lebih banyak dan, jika ada kad dalam fail, pek ditambah secara automatik: anda dibilkan untuk gabungan paling murah yang meliputi apa yang anda simpan. Tiada kad dalam fail? Muat naik baharu dijeda sehingga anda menambah satu; tiada apa-apa dipadam dan galeri anda terus menjual.",
      includedHeading: "Disertakan",
      packsHeading: "Pek storan tambahan",
      packPrice: "${price} sebulan",
      examplesHeading: "Cara pek dijumlahkan",
      example: "{plan} menyimpan {stored}: lebih {over}, dibilkan sebagai {mix} = ${price} sebulan",
      note: "Kami akan menghantar e-mel apabila anda mencapai 80% storan anda. Pek ditanggalkan apabila anda mengosongkan ruang.",
    },
    freeDownloads: {
      heading: "Galeri muat turun percuma",
      subtitle:
        "Kongsi galeri yang boleh dimuat turun secara percuma oleh pembeli anda. Setiap pelan termasuk foto setiap bulan; selepas itu $8 setiap 1,000 foto ($0.008 sekeping) pada setiap pelan.",
      line: "{count} foto sebulan",
      note: "Peruntukan bermula semula setiap bulan, dan galeri terus berfungsi melebihinya.",
    },
    example: {
      heading: "Berapa yang anda simpan",
      subtitle: "Pesanan UK yang dibayar dengan kad, melalui akaun Stripe anda sendiri, pada setiap pelan. Cuba jumlah yang lain.",
      orderLabel: "Nilai pesanan",
      order: "Pesanan",
      cardFee: "Yuran kad Stripe",
      cardFeeNote: "Dikenakan oleh Stripe pada akaun anda, cth. kad UK 1.5% + 20p",
      colPlan: "Pelan",
      colCommission: "Komisen Photohawk",
      colKeep: "Anda simpan kira-kira",
      planCell: "{plan} ({rate})",
      note: "Yuran Stripe bergantung pada kad: kad antarabangsa dan penukaran mata wang biasanya lebih mahal. Komisen kami ialah satu-satunya yuran Photohawk.",
    },
    cardFees: {
      heading: "Cara yuran kad berfungsi",
      intro: "Yuran kad dan pemprosesan pembayaran berfungsi sama seperti hari ini, berasingan daripada komisen kami.",
      items: [
        {
          id: "stripe",
          title: "Pada akaun Stripe anda sendiri",
          body: "Di UK, EU, AS dan negara Stripe yang lain, Stripe mengenakan yuran pemprosesan standardnya terus pada akaun Stripe anda.",
        },
        {
          id: "managed",
          title: "Di tempat Photohawk memproses pembayaran untuk anda",
          body: "Di Malaysia, Filipina, Mexico, Colombia, Ecuador dan negara lain di mana pembeli membayar dengan dompet tempatan, yuran pemprosesan pembayaran ditolak pada kadar tempatan berasaskan kos, seperti hari ini.",
        },
      ],
      linkLabel: "Lihat yuran kad mengikut negara",
    },
    compare: {
      heading: "Bandingkan setiap ciri",
      subtitle: "Semua dalam Photohawk Studio, pelan demi pelan. Studio berfungsi dalam pelayar web anda pada setiap pelan hari ini.",
      featureColumn: "Ciri",
      included: "Disertakan",
      notIncluded: "Tidak disertakan",
      linkLabel: "Bandingkan setiap ciri",
      sections: {
        selling: "Jualan",
        finding: "Mencari foto",
        galleries: "Galeri",
        brand: "Jenama anda",
        insights: "Cerapan",
        team: "Pasukan",
        uploading: "Muat naik",
        storage: "Storan",
        support: "Sokongan",
      },
      rows: {
        commission: { label: "Komisen pada jualan", note: "Satu-satunya yuran kami pada sesuatu jualan. Yuran kad berfungsi sama seperti hari ini." },
        products: { label: "Muat turun digital, cetakan dan produk", note: "Produk dan harga anda sendiri." },
        packages: { label: "Pakej dan himpunan", note: "" },
        coupons: { label: "Kupon", note: "" },
        preorder: { label: "Harga prapesanan", note: "" },
        whatsapp: { label: "Penghantaran WhatsApp (perkhidmatan Photohawk untuk pembeli)", note: "Tambahan pilihan yang dibayar oleh pembeli semasa pembayaran, supaya foto mereka tiba di WhatsApp sedia untuk dikongsi. Dijalankan oleh Photohawk pada setiap galeri; bukan tetapan yang anda kawal." },
        payouts: { label: "Pembayaran keluar", note: "Ke akaun Stripe anda sendiri, atau dibayar oleh Photohawk di tempat kami memproses pembayaran untuk anda." },
        revenueSplit: { label: "Pembahagian hasil", note: "Kongsi jualan galeri dengan jurugambar, kru, kelab atau penaja. Photohawk mengira setiap bahagian dan membayarnya untuk anda." },
        selfie: { label: "Carian swafoto", note: "" },
        bib: { label: "Carian nombor bib dan teks", note: "Cari foto mengikut nombor perlumbaan atau teks dalam foto." },
        time: { label: "Carian masa", note: "" },
        find: { label: "Disenaraikan dalam Cari Foto Anda", note: "Pembeli boleh menemui galeri awam anda di photohawk.com." },
        profiles: { label: "Carian yang disimpan", note: "Simpan swafoto, nombor atau teks dan lihat padanan baharu." },
        faceData: { label: "Data wajah untuk carian swafoto", note: "Tiada tarikh luput secara lalai, atau tempoh pilihan anda, mulai 1 November 2026." },
        galleries: { label: "Galeri dan koleksi", note: "Sebanyak yang anda perlukan." },
        private: { label: "Galeri peribadi dan kata laluan", note: "" },
        protection: { label: "Perlindungan pratonton", note: "Perlindungan tangkapan skrin dengan ketik untuk mendedahkan." },
        watermarks: { label: "Tera air", note: "" },
        freeDownloads: { label: "Foto muat turun percuma yang disertakan", note: "Peruntukan bermula semula setiap bulan." },
        freeOverage: { label: "Foto muat turun percuma melebihi peruntukan", note: "Galeri terus berfungsi." },
        video: { label: "Muat naik video", note: "" },
        address: { label: "Alamat Photohawk anda", note: "namaanda.photohawk.com" },
        customDomain: { label: "Domain tersuai", note: "Galeri anda di alamat web anda sendiri." },
        pages: { label: "Halaman", note: "Halaman anda sendiri di samping galeri anda." },
        emails: { label: "Templat e-mel tersuai", note: "Sunting e-mel yang diterima pembeli daripada setiap galeri." },
        logo: { label: "Logo dan warna anda", note: "Pada galeri, halaman dan e-mel anda." },
        noBadge: { label: "Tiada lencana “Powered by Photohawk”", note: "Pada Lite, lencana kecil dipaparkan di bahagian bawah galeri dan halaman log masuk anda." },
        live: { label: "Pelawat langsung", note: "Berapa ramai orang sedang melihat sesebuah galeri sekarang." },
        analytics: { label: "Analitik pelawat", note: "Pengguna dan lawatan dari semasa ke semasa, galeri demi galeri." },
        sales: { label: "Pesanan dan jualan dari semasa ke semasa", note: "" },
        collectionDownloads: { label: "Muat turun mengikut koleksi", note: "" },
        people: { label: "Ahli dalam ruang kerja anda", note: "Kolaborator galeri turut dikira. Ahli yang sudah berada dalam ruang kerja anda mengekalkan akses mereka." },
        roles: { label: "Peranan", note: "Akses pemilik, pentadbir, muat naik sahaja dan jualan sahaja." },
        collaborators: { label: "Kolaborator galeri", note: "" },
        webUploader: { label: "Pemuat naik web", note: "Menghantar banyak foto serentak dan bersambung semula dengan sendirinya." },
        previewsFirst: { label: "Pratonton dahulu", note: "Galeri dalam talian dengan pratonton ringan dalam beberapa minit sementara fail asal menyusul." },
        soldFirst: { label: "Foto terjual dahulu", note: "Foto yang dibeli atau dimuat turun sebelum fail asalnya dimuat naik akan dihantar seterusnya." },
        originalsBy: { label: "Tarikh “fail asal sedia pada” untuk pembeli", note: "Beritahu pembeli bila fail asal akan sedia." },
        ftp: { label: "Pelayan FTP atas permintaan", note: "Jika FTP amat diperlukan: pelayan anda sendiri, dimulakan daripada pemuat naik web. Ia dimatikan secara automatik selepas 1 jam tanpa aktiviti." },
        cloudImport: { label: "Import daripada Dropbox dan Google Drive", note: "" },
        studioApp: { label: "Aplikasi Photohawk Studio untuk Mac, iPhone dan iPad", note: "Studio berfungsi dalam pelayar web anda hari ini." },
        included: { label: "Storan disertakan", note: "" },
        packs: { label: "Storan tambahan", note: "Ditambah secara automatik jika ada kad dalam fail, dibilkan sebagai gabungan paling murah." },
        you: { label: "Sokongan untuk ruang kerja anda", note: "" },
        buyers: { label: "Photohawk menjawab pembeli anda", note: "Soalan pembeli dihantar kepada sokongan Photohawk, melainkan anda menetapkan alamat anda sendiri." },
      },
      values: {
        rate: "{rate}",
        storage: "{storage}",
        perMonth: "{n} sebulan",
        upToPeople: "Sehingga {n}",
        unlimited: "Tanpa had",
        upToDomains: "Sehingga {n}",
        months: "{n} bulan",
        fromNovember: "Mulai November 2026",
        packs: "Dari ${usd} setiap {gb} GB sebulan",
        overage: "${usd} setiap 1,000",
        supportHelp: "Pusat bantuan",
        supportEmail: "E-mel",
        supportPriority: "Keutamaan",
      },
    },
    faq: {
      heading: "Soalan",
      items: [
        {
          q: "Adakah yuran kad disertakan?",
          a: "Tidak. Yuran kad dikenakan oleh penyedia pembayaran sama seperti hari ini: oleh Stripe pada akaun anda sendiri, atau ditolak pada kadar tempatan berasaskan kos di tempat Photohawk memproses pembayaran untuk anda. Komisen kami (16% pada Lite, 14% pada Pro, 12% pada Max) ialah satu-satunya yuran Photohawk.",
        },
        {
          q: "Adakah carian swafoto disertakan?",
          a: "Ya, pada setiap pelan, termasuk Lite. Begitu juga carian nombor bib dan masa, perlindungan pratonton dan pemuat naik web. Aplikasi Photohawk Studio untuk Mac, iPhone dan iPad akan tersedia pada setiap pelan apabila dilancarkan pada November 2026.",
        },
        {
          q: "Bolehkah saya menukar pelan bila-bila masa?",
          a: "Ya. Pelan adalah bulanan, tanpa kontrak tahunan. Apabila anda beralih ke pelan dengan kadar lebih rendah, kadar itu berkuat kuasa mulai bulan penuh pertama anda padanya. Penurunan pelan berkuat kuasa pada akhir bulan.",
        },
        {
          q: "Saya ada perjanjian khas. Adakah ia berubah?",
          a: "Tidak. Perjanjian khas sedia ada diteruskan sehingga tamat.",
        },
        {
          q: "Apakah yang berlaku kepada muat turun percuma melebihi peruntukan?",
          a: "Galeri muat turun percuma anda terus berfungsi. Setiap bulan pelan anda termasuk 1,000 foto pada Lite, 5,000 pada Pro dan 10,000 pada Max. Selepas itu $8 setiap 1,000 foto ($0.008 sekeping) pada setiap pelan.",
        },
        {
          q: "Bagaimana jika saya perlukan lebih banyak storan?",
          a: "Lite termasuk 150 GB, Pro 1 TB dan Max 3 TB. Melebihi itu, jika ada kad dalam fail, pek ditambah secara automatik: +100 GB untuk $5, +1 TB untuk $25 atau +5 TB untuk $100 sebulan, dibilkan sebagai gabungan paling murah yang meliputi apa yang anda simpan. Tanpa kad dalam fail, muat naik baharu dijeda sehingga anda menambah satu: tiada apa-apa dipadam dan galeri anda terus menjual.",
        },
        {
          q: "Apakah yang saya dapat dengan Max?",
          a: "Kadar terendah, 12%, ditambah storan 3 TB, 10,000 foto muat turun percuma sebulan, muat naik video, ahli pasukan tanpa had, analitik pelawat dan sokongan keutamaan.",
        },
        {
          q: "Berapa ramai orang boleh berada dalam ruang kerja saya?",
          a: "Sehingga 3 orang pada Lite, sehingga 10 pada Pro dan seramai yang anda mahu pada Max. Sudah ada lebih ramai orang dalam ruang kerja anda? Mereka mengekalkan akses mereka: had hanya terpakai apabila anda menjemput orang baharu.",
        },
        {
          id: "ftp",
          q: "Bolehkah saya masih memuat naik melalui FTP?",
          a: "Ya, jika FTP amat diperlukan. Pemuat naik web kini cara standard untuk memuat naik, pada setiap pelan: ia menghantar banyak foto serentak, jauh lebih banyak daripada aplikasi FTP seperti FileZilla (2 serentak secara lalai, dan tidak lebih daripada 10), dan bersambung semula dengan sendirinya jika sambungan terputus. Jika anda masih memerlukan FTP, setiap ahli ruang kerja anda, pada setiap pelan, boleh memulakan pelayan FTP atas permintaan mereka sendiri daripada pemuat naik web, pada alamat Photohawk peribadi yang tidak pernah berubah; ia dimatikan secara automatik selepas 1 jam tanpa aktiviti. Pelayan FTP kongsi ditutup pada 1 November 2026.",
        },
        {
          q: "Adakah Photohawk Friends & Family sebahagian daripada pelan?",
          a: "Tidak. Friends & Family, aplikasi kami untuk foto keluarga anda, ialah aplikasi berasingan yang akan datang tidak lama lagi, dan bukan sebahagian daripada mana-mana pelan Studio. Menguruskan galeri dan muat naik anda? Itu Photohawk Studio: ia berfungsi dalam pelayar web anda pada setiap pelan hari ini, dan aplikasi Studio untuk Mac, iPhone dan iPad dilancarkan pada November 2026.",
        },
      ],
    },
  },

  changes: {
    seo: {
      title: "Harga berubah pada 1 November 2026 | Photohawk",
      description:
        "Mulai 1 November 2026, komisen diturunkan pada setiap pelan Photohawk, Pro dan Max mendapat lebih banyak storan, dan foto muat turun percuma disertakan setiap bulan.",
    },
    hero: {
      badge: "Kemas kini harga",
      title: "Harga berubah pada 1 November 2026.",
      titleHighlight: "Komisen lebih rendah pada setiap pelan.",
      subtitle:
        "Kadar setiap pelan diturunkan, Pro dan Max mendapat lebih banyak storan, dan foto muat turun percuma kini disertakan setiap bulan. Anda tidak perlu berbuat apa-apa.",
      ctaPrimary: "Lihat harga baharu",
      ctaSecondary: "Bandingkan pelan anda",
    },
    why: {
      heading: "Apa yang menjadi lebih baik",
      items: [
        {
          id: "rates",
          title: "Komisen lebih rendah pada setiap pelan",
          body: "Kadar setiap pelan turun, sebanyak 1.5 hingga 9 mata. Pay as you go dan Lite menjadi Lite baharu, tanpa yuran bulanan dan 16%, Pro turun ke 14%, dan Enterprise, kini dinamakan Max, turun ke 12%.",
        },
        {
          id: "storage",
          title: "Lebih banyak storan pada setiap pelan",
          body: "Lite naik daripada 100 GB kepada 150 GB, Pro daripada 500 GB kepada 1 TB dan Max daripada 1 TB kepada 3 TB. 25 GB Pay as you go menjadi 150 GB pada Lite.",
        },
        {
          id: "downloads",
          title: "Foto muat turun percuma disertakan",
          body: "Hari ini setiap foto dalam galeri muat turun percuma dikenakan bayaran. Mulai 1 November, 1,000 hingga 10,000 sebulan disertakan, kemudian $8 setiap 1,000: sama atau kurang daripada kadar hari ini pada setiap pelan.",
        },
        {
          id: "simple",
          title: "Harga lebih mudah",
          body: "Tiga pelan dan bukan empat, satu komisen bagi setiap pelan, dan storan tambahan dalam pek yang mudah.",
        },
        {
          id: "order",
          title: "Yuran transaksi tidak berubah",
          body: "Yuran transaksi berfungsi sama seperti hari ini, berasingan daripada komisen kami.",
        },
        {
          id: "deals",
          title: "Perjanjian anda dihormati",
          body: "Jika anda ada perjanjian khas, ia diteruskan sehingga tamat.",
        },
      ],
      honestHeading: "Satu-satunya harga yang naik",
      honestBody:
        "Enterprise, kini dinamakan Max, ialah $49 sebulan (sebelum ini $44.99) mulai pembaharuan pertama anda pada atau selepas 1 November. Sebagai balasan, ia termasuk 3 TB dan bukan 1 TB, 10,000 foto muat turun percuma sebulan dan kadar terendah, 12% dan bukan 13.5%: sebaik sahaja anda menjual kira-kira $270 sebulan, kadar yang lebih rendah lebih daripada menampung perbezaannya. Pro turun daripada $29.99 kepada $29.",
    },
    when: {
      heading: "Apa yang berlaku pada 1 November",
      subtitle: "Satu tarikh untuk semua. Ruang kerja anda berpindah ke pelan baharunya secara automatik.",
      moves: {
        payg: "Masih $0 sebulan. 16% dan bukan 25%, dan 150 GB dan bukan 25 GB.",
        lite: "Lite kekal, dan kini percuma: tiada lagi $16.99 sebulan, 16% dan bukan 18.5%, dan lebih banyak storan: 150 GB dan bukan 100 GB. Jika anda perlukan lebih, storan tambahan dari $5 setiap 100 GB sebulan. Galeri anda mengekalkan logo dan warna anda, dengan lencana kecil “Powered by Photohawk”.",
        pro: "$29 dan bukan $29.99 sebulan, 14% dan bukan 16%, dan 1 TB dan bukan 500 GB.",
        enterprise: "Enterprise dinamakan semula Max — pelan yang sama, nama baharu, kini dengan 3 TB dan kadar 12%. Ia $49 sebulan dan bukan $44.99 mulai pembaharuan pertama anda pada atau selepas 1 November.",
      },
      points: [
        {
          id: "action",
          title: "Tiada tindakan diperlukan",
          body: "Ruang kerja anda berpindah pada 1 November 2026. Galeri, harga dan pembayaran keluar diteruskan seperti biasa.",
        },
        {
          id: "month",
          title: "Peraturan bulan penuh",
          body: "Beralih ke pelan dengan kadar lebih rendah dan kadar itu berkuat kuasa mulai bulan penuh pertama anda padanya. Penurunan pelan berkuat kuasa pada akhir bulan.",
        },
        {
          id: "change",
          title: "Menukar pelan",
          body: "Anda boleh menukar pelan bila-bila masa daripada Pengebilan dalam ruang kerja anda.",
        },
        {
          id: "deals",
          title: "Perjanjian khas",
          body: "Perjanjian khas sedia ada diteruskan sehingga tamat.",
        },
        {
          id: "privacy",
          title: "Carian swafoto dan privasi",
          body: "Mulai 1 November, anda memilih berapa lama data wajah yang digunakan untuk carian swafoto disimpan: tiada tarikh luput secara lalai, atau tempoh selepas setiap foto dimuat naik. Foto, jualan dan carian nombor bib anda tidak terjejas.",
        },
        {
          id: "team",
          title: "Pasukan anda",
          body: "Lite termasuk sehingga 3 orang, Pro sehingga 10 dan Max tanpa had. Sudah ada lebih ramai orang dalam ruang kerja anda? Mereka mengekalkan akses mereka — had hanya terpakai apabila menambah orang baharu.",
        },
      ],
    },
    ftp: {
      date: "1 November 2026",
      heading: "Pemuat naik web kini cara standard untuk memuat naik",
      body: "Pelayan FTP kongsi yang sentiasa aktif ditutup pada 1 November 2026. Pemuat naik web lebih pantas daripada FTP dan menggunakan sambungan anda sepenuhnya, pada setiap pelan. Jika FTP amat diperlukan, anda boleh memulakan pelayan anda sendiri atas permintaan.",
      items: [
        {
          id: "web",
          title: "Pemuat naik web, pada setiap pelan",
          body: "Ia menghantar banyak foto serentak, jauh lebih banyak daripada aplikasi FTP seperti FileZilla (2 serentak secara lalai, dan tidak lebih daripada 10), dan terus menyesuaikan diri untuk menggunakan lebar jalur anda sepenuhnya, dan jika sambungan terputus ia bersambung semula dengan sendirinya. Pada setiap pelan, pratonton ringan boleh dimuat naik dahulu, jadi galeri anda dalam talian dalam beberapa minit.",
        },
        {
          id: "ondemand",
          title: "Jika FTP amat diperlukan: pada setiap pelan",
          body: "Tersedia sekarang, pada setiap pelan: mulakan pelayan FTP atas permintaan anda sendiri daripada pemuat naik web, pada alamat Photohawk peribadi yang tidak pernah berubah. Ia dimatikan secara automatik selepas 1 jam tanpa aktiviti.",
        },
        {
          id: "studio",
          title: "Photohawk Studio, pada setiap pelan",
          body: "Aplikasi pemuat naik Photohawk untuk ruang kerja anda, pada desktop dan iOS.",
        },
      ],
    },
    privacyLink: "Dasar penyimpanan data biometrik",
    included: {
      heading: "Apa yang disertakan pada setiap pelan",
      everyPlan:
        "Setiap pelan: carian swafoto, carian nombor bib dan masa, perlindungan pratonton, pemuat naik web dengan pratonton dahulu, dan pelayan FTP atas permintaan anda sendiri.",
      upgrades: "Pro dan Max menambah analitik pelawat dan domain tersuai. Max menambah muat naik video.",
    },
    compare: {
      heading: "Hari ini berbanding mulai 1 November",
      subtitle: "Pelan demi pelan, dalam dolar AS.",
      rows: {
        fee: "Yuran bulanan",
        commission: "Komisen pada jualan",
        storage: "Storan disertakan",
        downloads: "Foto muat turun percuma",
        extraStorage: "Storan tambahan",
      },
      downloadsToday: "{price} sekeping",
      downloadsNext: "{count} sebulan disertakan, kemudian $8 setiap 1,000",
      extraStorageToday: "$0.023 setiap GB sebulan",
      extraStorageNext: "Pek: +100 GB $5, +1 TB $25, +5 TB $100 sebulan",
    },
    countries: {
      heading: "Maksudnya di tempat anda menjual",
      subtitle: "Contoh dalam mata wang anda. Pilih negara.",
      selectLabel: "Negara",
      examplesBadge: "Contoh",
      orderHeading: "Pesanan biasa: {amount}",
      orderSubtitle: "Purata pesanan {inCountry}, dibayar dengan kad. Yuran kad {fee}, {kind}.",
      kind: {
        stripe: "dikenakan oleh Stripe pada akaun anda",
        managed: "ditolak di tempat Photohawk memproses pembayaran",
      },
      colPlan: "Pelan anda",
      colCommission: "Komisen Photohawk",
      colKeep: "Anda simpan",
      perOrderMore: "+{amount} setiap pesanan",
      monthlyHeading: "Jualan sebulan, semuanya sekali",
      monthlySubtitle:
        "Yuran pelan, komisen dan yuran kad bersama, sebagai bahagian daripada jualan anda, pada pelan yang paling murah di setiap tahap.",
      level: "{amount} jualan sebulan",
      onPlan: "pada {plan}",
      keepMore: "Anda simpan kira-kira {amount} lebih sebulan",
      disclaimer:
        "Contoh sahaja, berdasarkan nilai purata pesanan dan ditukar pada kadar pertukaran tetap (Oktober 2026). Angka anda bergantung pada pesanan, kad dan pelan anda. Caj muat turun percuma dan storan tidak disertakan.",
    },
    faq: {
      heading: "Soalan",
      items: [
        {
          q: "Adakah saya perlu berbuat apa-apa?",
          a: "Tidak. Pada 1 November 2026 ruang kerja anda berpindah ke pelan baharunya secara automatik. Galeri, harga dan pembayaran keluar anda diteruskan seperti biasa.",
        },
        {
          q: "Saya menggunakan Lite. Apa yang berlaku?",
          a: "Lite kekal, dan kini percuma: mulai 1 November tiada lagi $16.99 sebulan, kadar anda 16% dan bukan 18.5%, dan anda mendapat lebih banyak storan: 150 GB dan bukan 100 GB. Jika anda menyimpan lebih, storan tambahan dari $5 setiap 100 GB sebulan, ditambah secara automatik apabila ada kad dalam fail (tanpanya, muat naik baharu dijeda sehingga anda menambah kad; tiada apa-apa dipadam). Galeri anda mengekalkan logo dan warna anda, dengan lencana kecil “Powered by Photohawk”.",
        },
        {
          q: "Bagaimana jika saya menyimpan lebih daripada yang disertakan dalam pelan saya?",
          a: "Dengan kad dalam fail, pek storan ditambah secara automatik, dari $5 setiap 100 GB sebulan, dibilkan sebagai gabungan paling murah yang meliputi apa yang anda simpan. Tanpa kad dalam fail, muat naik baharu dijeda sehingga anda menambah satu: tiada apa-apa dipadam dan galeri anda terus menjual.",
        },
        {
          q: "Saya menggunakan Enterprise. Apa yang berlaku?",
          a: "Enterprise dinamakan semula Max: pelan yang sama, nama baharu. Mulai 1 November kadar anda 12% dan bukan 13.5% dan anda mendapat 3 TB dan bukan 1 TB. Harga baharu, $49 sebulan dan bukan $44.99, terpakai mulai pembaharuan pertama anda pada atau selepas 1 November.",
        },
        {
          q: "Berapa ramai orang boleh berada dalam ruang kerja saya?",
          a: "Sehingga 3 orang pada Lite, sehingga 10 pada Pro dan seramai yang anda mahu pada Max. Sudah ada lebih ramai orang dalam ruang kerja anda? Mereka mengekalkan akses mereka: had hanya terpakai apabila anda menjemput orang baharu.",
        },
        {
          id: "ftp",
          q: "Apa yang berlaku kepada FTP?",
          a: "Pelayan FTP kongsi yang sentiasa aktif ditutup pada 1 November 2026, dan pemuat naik web kini cara standard untuk memuat naik, pada setiap pelan. Jika FTP amat diperlukan, setiap ahli ruang kerja anda, pada setiap pelan, sudah boleh memulakan pelayan FTP atas permintaan mereka sendiri daripada pemuat naik web, pada alamat Photohawk peribadi yang tidak pernah berubah; ia dimatikan secara automatik selepas 1 jam tanpa aktiviti.",
        },
        {
          q: "Adakah perjanjian khas saya akan berubah?",
          a: "Tidak. Perjanjian khas sedia ada diteruskan sehingga tamat.",
        },
        {
          q: "Jika saya menukar pelan, bilakah kadar baharu bermula?",
          a: "Kadar lebih rendah sesuatu pelan berkuat kuasa mulai bulan penuh pertama anda padanya. Penurunan pelan berkuat kuasa pada akhir bulan.",
        },
        {
          q: "Adakah yuran kad berubah?",
          a: "Tidak. Yuran kad dan pemprosesan pembayaran berfungsi sama seperti hari ini, berasingan daripada komisen kami.",
        },
        {
          q: "Adakah Photohawk Friends & Family sebahagian daripada pelan?",
          a: "Tidak. Friends & Family, aplikasi kami untuk foto keluarga anda, ialah aplikasi berasingan yang akan datang tidak lama lagi, dan bukan sebahagian daripada mana-mana pelan Studio. Menguruskan galeri dan muat naik anda? Itu Photohawk Studio: ia berfungsi dalam pelayar web anda pada setiap pelan hari ini, dan aplikasi Studio untuk Mac, iPhone dan iPad dilancarkan pada November 2026.",
        },
      ],
    },
    cta: {
      heading: "Lihat harga baharu sepenuhnya",
      body: "Pelan, storan, muat turun percuma dan yuran kad mengikut negara, semuanya dalam satu halaman.",
      button: "Lihat harga baharu",
    },
  },

  app: {
    badge: "Akan datang",
    platform: "iPhone dahulu",
    heading: "Photohawk Friends & Family, untuk foto keluarga anda",
    intro: "Friends & Family ialah aplikasi kami untuk semua foto keluarga anda: yang anda ambil dan yang anda kongsi. Ia aplikasi berasingan yang akan datang tidak lama lagi, dan bukan sebahagian daripada pelan Studio.",
    points: [
      { id: "private", title: "Cari foto keluarga anda, secara peribadi", body: "Susun dan cari foto orang yang anda sayangi, terus pada telefon anda." },
      { id: "albums", title: "Album di mana setiap orang melihat foto mereka sendiri", body: "Kongsi album dan setiap orang hanya melihat foto yang ada mereka, dipadankan melalui swafoto: teknologi yang sama seperti galeri Photohawk." },
      { id: "event", title: "Kumpul foto semua orang daripada sesuatu acara", body: "Himpunkan foto yang diambil oleh rakan dan keluarga, semuanya di satu tempat." },
      { id: "prints", title: "Cetakan dan buku foto", body: "Jadikan yang terbaik cetakan dan buku foto." },
    ],
    cta: "Maklumkan saya",
    ctaNote: "Senarai menunggu dibuka tidak lama lagi.",
    phoneAlbum: "Saya sahaja",
    phoneAlbumCount: "12 foto",
  },
};

export default ms;
